/**
 * MetricDisplay - Shows a metric value with confidence indicator
 * Pure presentational component wrapped with React.memo
 */

import { memo } from 'react';
import type { MetricSnapshot } from '../../../types';
import { formatCurrency, formatNumber } from './formatters';

interface MetricDisplayProps {
  metric: MetricSnapshot;
  label: string;
}

function MetricDisplayComponent({ metric, label }: MetricDisplayProps) {
  const isMonetary = metric.unit === 'USD' || metric.unit === '$';
  const displayValue = isMonetary
    ? formatCurrency(metric.value)
    : metric.unit === '%'
      ? `${metric.value}%`
      : `${formatNumber(metric.value)} ${metric.unit || ''}`.trim();

  return (
    <div>
      <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
      <div className="text-lg font-semibold text-gray-900">{displayValue}</div>
      <div className="flex items-center gap-2 mt-0.5">
        {metric.is_estimated && <span className="text-xs text-amber-600">Est.</span>}
        <span
          className={`text-xs ${
            metric.confidence === 'high'
              ? 'text-green-600'
              : metric.confidence === 'medium'
                ? 'text-amber-600'
                : 'text-red-600'
          }`}
        >
          {metric.confidence} confidence
        </span>
      </div>
    </div>
  );
}

export const MetricDisplay = memo(MetricDisplayComponent);
